import { useEffect, useMemo, useRef } from 'react'
import { IMAGES } from '../../constants/image'
import './SplashScreen.css'
import { DefaultScreenProps } from '../defaultScreenProps'
import { isTvMode } from '../../utils/tvMode'
import { COPY, DYNAMIC_COPY } from '../../constants/copy'
import { runsService } from '../../services/RunsService/RunsService'

type SplashScreenProps = DefaultScreenProps & {
  onTrolleyClick: () => void
  onCowClick: () => void
}

export const SplashScreen = ({
  onTrolleyClick,
  onCowClick,
}: SplashScreenProps) => {
  const trolleyRef = useRef<HTMLDivElement>(null)
  const cowRef = useRef<HTMLDivElement>(null)
  const tv = isTvMode()
  const history = useMemo(() => runsService.load(), [])

  // Warm the next two screens' ~1 MB paintings while the player reads
  // the title, so the intro and the stats trolley never open on black.
  useEffect(() => {
    for (const src of [IMAGES.intro, IMAGES.trolley]) {
      const img = new Image()
      img.src = src
    }
  }, [])

  // TV mode: the remote has no pointer, so focus must land on the start
  // element immediately. Enter then fires the existing role=button
  // keydown handler — no new click paths. The cow is deliberately NOT
  // reachable by D-pad: inside the Fire TV shell she is a link out of
  // the free game.
  useEffect(() => {
    if (!isTvMode()) return

    trolleyRef.current?.focus()

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        trolleyRef.current?.focus()
      } else if (event.key === 'Enter') {
        // Safety net: if focus was somehow lost, Enter still starts the
        // game. When the trolley is focused its own handler runs instead
        // (this document handler skips it to avoid double-fires).
        const active = document.activeElement
        if (active !== trolleyRef.current && active !== cowRef.current) {
          event.preventDefault()
          onTrolleyClick()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onTrolleyClick])

  return (
    <div className="splashScreenContainer">
      <img
        className="splashScreenImage"
        src={IMAGES.trolleyAndCow}
        alt="Trollilopolis title art: a menacing trolley facing off with a cow"
      />
      <div className="clickableAreas">
        <div
          className="trolleyArea"
          ref={trolleyRef}
          role="button"
          tabIndex={0}
          aria-label="Click the trolley to start the game"
          onClick={onTrolleyClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onTrolleyClick()
            }
          }}
        ></div>
        <div
          className="cowArea"
          ref={cowRef}
          role="button"
          tabIndex={tv ? -1 : 0}
          aria-hidden={tv}
          aria-label="Click the cow to visit Heifferopolis"
          onClick={onCowClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onCowClick()
            }
          }}
        ></div>
      </div>
      <div className="startPill" aria-hidden="true">
        <span className="startPillText">
          {tv ? COPY.START_TV : COPY.START_WEB}
        </span>
        {history && (
          <span className="lastRun">
            {DYNAMIC_COPY.LAST_RUN(history.lastGrade, history.bestGrade)}
          </span>
        )}
      </div>
    </div>
  )
}
